// Inline comments thread for a community patch. Reads via direct
// Supabase RLS (no Worker hop needed for simple CRUD).
//
// The voice-anchor field on the schema (voice_const_name) is honoured
// here: comments tagged at a voice show "@bass:" before the body so
// they read as a thread per stem. The composer defaults to a
// whole-patch comment; tagging a specific voice is a v2 affordance
// driven from the editor side (not wired in this rail surface).

import { useCallback, useEffect, useState } from 'react';
import {
  addComment,
  deleteComment,
  listComments,
  type Comment,
} from '../../cloud/comments';
import { useSession } from '../../auth/useSession';

export type CommentsThreadProps = {
  patchId: string;
};

export function CommentsThread({ patchId }: CommentsThreadProps) {
  const { session } = useSession();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  const refresh = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    listComments(patchId)
      .then((rows) => {
        if (cancelled) return;
        setComments(rows);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patchId]);

  useEffect(() => {
    return refresh();
  }, [refresh]);

  const submit = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed || !session) return;
    setPosting(true);
    setError(null);
    try {
      const c = await addComment({ patchId, voiceConstName: null, body: trimmed });
      setComments((prev) => [...prev, c]);
      setDraft('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPosting(false);
    }
  }, [draft, patchId, session]);

  const remove = useCallback(async (commentId: string) => {
    try {
      await deleteComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  return (
    <div className="space-y-2">
      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500">
        Comments
      </div>

      {loading ? (
        <div className="text-[10px] text-zinc-600 py-2">Loading…</div>
      ) : comments.length === 0 ? (
        <div className="text-[10px] text-zinc-600 py-2 italic">
          No comments yet.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {comments.map((c) => (
            <li
              key={c.id}
              className="text-[11px] bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5"
            >
              <div className="flex items-baseline gap-2">
                <span className="text-cyan-300 text-[10px]">@{c.authorHandle}</span>
                {c.voiceConstName && (
                  <span className="text-yellow-300 text-[10px] font-mono">
                    on {c.voiceConstName}
                  </span>
                )}
                <span className="text-zinc-600 text-[9px] tabular-nums ml-auto">
                  {formatTime(c.createdAt)}
                </span>
                {session?.user.id === c.authorId && (
                  <button
                    type="button"
                    onClick={() => void remove(c.id)}
                    aria-label="Delete comment"
                    title="Delete"
                    className="text-zinc-600 hover:text-rose-400 text-[11px] leading-none px-1"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="text-zinc-200 mt-1 whitespace-pre-wrap break-words">
                {c.body}
              </div>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <div className="text-[10px] text-rose-300">{error}</div>
      )}

      {session ? (
        <div className="flex flex-col gap-1.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a comment…"
            rows={2}
            maxLength={2000}
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-300/50 focus:outline-none rounded-sm text-[11px] text-zinc-100 placeholder:text-zinc-600 px-2 py-1.5 resize-none"
          />
          <button
            type="button"
            onClick={() => void submit()}
            disabled={posting || draft.trim().length === 0}
            className="self-end text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 bg-cyan-300 text-zinc-950 font-semibold rounded-sm disabled:opacity-30"
          >
            {posting ? 'Posting…' : 'Post'}
          </button>
        </div>
      ) : (
        <div className="text-[10px] text-zinc-600 italic">
          Sign in to comment.
        </div>
      )}
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Compact "MMM dd · hh:mm" — local time. Patches are short-lived
  // social objects, no need for relative-time complexity yet.
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
